import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { FileText, Clock, Truck, User, ChevronDown, ChevronUp, Pencil, X, Check, Camera, Trash2 } from 'lucide-react';

export default function MyLogs() {
  const { logs, customers, updateLog, deleteLog, user, isAdmin } = useApp();
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef();

  const visible = isAdmin ? logs : logs.filter(l => l.userId === user?.uid);

  const startEdit = (log) => { setEditing(log.id); setEditForm({ ...log }); setExpanded(log.id); };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => { await updateLog(editing, editForm); setEditing(null); setEditForm({}); };
  const setField = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setEditForm(f => ({ ...f, photoPreview: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleDelete = async (id) => {
    if (confirmDelete !== id) { setConfirmDelete(id); return; }
    await deleteLog(id);
    setConfirmDelete(null);
    if (expanded === id) setExpanded(null);
  };

  return (
    <div className="page">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, marginBottom: 4 }}>My Work Logs</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Your submitted daily work entries.</p>
      </div>

      {visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <FileText size={36} color="var(--text-dim)" style={{ marginBottom: 12 }} />
          <p style={{ color: 'var(--text-muted)' }}>No logs submitted yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(log => (
            <div key={log.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Header */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer' }}
                onClick={() => editing !== log.id && setExpanded(expanded === log.id ? null : log.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 16 }}>{log.customerName}</span>
                    <span className="tag tag-orange">{log.date}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 14, marginTop: 4, color: 'var(--text-muted)', fontSize: 13 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={12} />{log.driverName}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={12} />{log.hours} hrs</span>
                    {log.truckNumber && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Truck size={12} />#{log.truckNumber}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => startEdit(log)} className="btn-secondary"
                    style={{ padding: '5px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Pencil size={12} /> Edit
                  </button>
                  {confirmDelete === log.id ? (
                    <>
                      <button onClick={() => handleDelete(log.id)} className="btn-danger" style={{ padding: '5px 12px', fontSize: 12 }}>
                        Confirm
                      </button>
                      <button onClick={() => setConfirmDelete(null)} className="btn-secondary" style={{ padding: '5px 10px', fontSize: 12 }}>
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleDelete(log.id)} className="btn-danger" style={{ padding: '5px 10px' }}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                {expanded === log.id ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
              </div>

              {/* Expanded panel */}
              {expanded === log.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px', background: 'var(--bg3)' }}>
                  {editing === log.id ? (
                    <div>
                      <div style={{ marginBottom: 14 }}>
                        <label>Slip Photo</label>
                        {editForm.photoPreview ? (
                          <div style={{ position: 'relative', marginTop: 6 }}>
                            <img src={editForm.photoPreview} alt="slip" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 'var(--radius)' }} />
                            <button type="button" onClick={() => setEditForm(f => ({ ...f, photoPreview: null }))}
                              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div onClick={() => fileRef.current.click()} style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius)', padding: '16px', textAlign: 'center', cursor: 'pointer', marginTop: 6 }}>
                            <Camera size={20} color="var(--text-dim)" style={{ marginBottom: 4 }} />
                            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Tap to add or replace photo</p>
                          </div>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} style={{ display: 'none' }} />
                      </div>
                      <div className="grid-2">
                        <div className="field"><label>Driver Name</label><input value={editForm.driverName || ''} onChange={setField('driverName')} /></div>
                        <div className="field"><label>Date</label><input type="date" value={editForm.date || ''} onChange={setField('date')} /></div>
                      </div>
                      <div className="grid-2">
                        <div className="field">
                          <label>Customer</label>
                          <select value={editForm.customerId || ''} onChange={e => {
                            const c = customers.find(c => c.id === e.target.value);
                            setEditForm(f => ({ ...f, customerId: e.target.value, customerName: c ? c.name : '' }));
                          }}>
                            <option value="">— Select —</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div className="field"><label>Hours</label><input type="number" step="0.5" min="0" value={editForm.hours || ''} onChange={setField('hours')} /></div>
                      </div>
                      <div className="field"><label>Truck Number</label><input value={editForm.truckNumber || ''} onChange={setField('truckNumber')} /></div>
                      <div className="field"><label>Work Description</label><textarea value={editForm.workDescription || ''} onChange={setField('workDescription')} rows={3} /></div>
                      <div className="field"><label>Additional Notes</label><textarea value={editForm.notes || ''} onChange={setField('notes')} rows={2} /></div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn-primary" onClick={saveEdit} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Save Changes</button>
                        <button type="button" className="btn-secondary" onClick={cancelEdit} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><X size={14} /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {log.photoPreview && (
                        <img src={log.photoPreview} alt="slip" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 'var(--radius)', marginBottom: 14 }} />
                      )}
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div>
                          <span style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Work Description</span>
                          <p style={{ marginTop: 3, fontSize: 14 }}>{log.workDescription}</p>
                        </div>
                        {log.notes && (
                          <div>
                            <span style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Notes</span>
                            <p style={{ marginTop: 3, fontSize: 14, color: 'var(--text-muted)' }}>{log.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
